import { useState, useCallback, useEffect, useRef } from 'react';
import { apiClient } from '@/app/api/client';
import { articleAiApi } from '@/app/api/article-ai';
import { ChatMessage } from '@contentgrowth/llm-service/ui/react';

/**
 * IMPORTANT: this is shared backend interaction logic, now we have different ways to interact with backend
 * 1. History message loading: we can give an endpoint to load history message if we want by given a value
 *    to historyEndpoint, as AI assistant do
 * 2. Chat: we can give a value to apiEndpoint so we will use that endpoint to send current message to backend
 *    and get response
 *    Or we can set handler to do custom chat (control what to send, etc.), as in AI Mode Chat do for article editing
 */
interface UseChatOptions {
  apiEndpoint?: string;
  historyEndpoint?: string;
  enabled?: boolean; // Only load history when enabled (e.g., when dialog is open)
  onResponse?: (response: any) => (string | Partial<ChatMessage> | null);
  inputRef?: React.RefObject<{ focus: () => void }>; // Reference to input for refocusing
  handler?: (messages: ChatMessage[], signal?: AbortSignal) => Promise<any>;
  initialMessages?: ChatMessage[]; // Legacy: initial messages to display
  initialContext?: Record<string, any>; // Context to pass to history endpoint
}

interface ChatError {
  message: string;
  retryable?: boolean;
  isInitError?: boolean; // True if error occurred during initialization
}

export const useChat = ({
  apiEndpoint,
  historyEndpoint,
  enabled = true,
  onResponse = (data) => data.content,
  inputRef,
  handler, // Legacy parameter
  initialMessages = [], // Legacy parameter
  initialContext,
}: UseChatOptions) => {
  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ChatError | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const activeMessageIdRef = useRef<string | number | null>(null);

  const stop = useCallback(() => {
    if (abortControllerRef.current) {
      // Signal the abort
      abortControllerRef.current.abort();

      // Stop the backend process via message ID (fire and forget)
      if (activeMessageIdRef.current) {
        console.log('[useChat] Cancelling message ID:', activeMessageIdRef.current);
        articleAiApi.cancelChat(String(activeMessageIdRef.current));
        activeMessageIdRef.current = null;
      }

      abortControllerRef.current = null;
      setIsLoading(false);
      // We rely on the promise rejection in sendMessage to handle state cleanup
      // This avoids race conditions between stop() and the catch block
    }
  }, []);

  // Load history function - can be called for retry
  const loadHistory = useCallback(async () => {
    if (!historyEndpoint) return;

    setIsLoading(true);
    setError(null);
    setMessages([]);

    try {
      const response = await apiClient.post(historyEndpoint, initialContext || {});
      const loadedMessages = response.data.messages || [];
      setMessages(loadedMessages);
      setHasLoaded(true);
    } catch (err: any) {
      const errorData = err.response?.data;
      const errorMessage = errorData?.message || err.message || 'Failed to connect to AI service.';
      const retryable = errorData?.retryable !== false;
      setError({ message: errorMessage, retryable, isInitError: true });
    }
    setIsLoading(false);
  }, [historyEndpoint, initialContext]);

  // Retry init function for retry button
  const retryInit = useCallback(() => {
    setHasLoaded(false); // Reset so loadHistory will run
    loadHistory();
  }, [loadHistory]);

  // Fetch initial chat history when enabled (e.g., when dialog opens)
  useEffect(() => {
    // Skip if no historyEndpoint provided (legacy mode)
    if (!historyEndpoint) return;

    // Reset state when dialog is closed
    if (!enabled) {
      setHasLoaded(false);
      setError(null);
      setMessages([]); // Clear messages when closed
      setInput(''); // Clear input when closed
      return;
    }

    // Don't reload if already loaded
    if (hasLoaded) return;

    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, historyEndpoint, hasLoaded, initialContext]); // Removed initialMessages to prevent infinite loop

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  const sendMessage = useCallback(async (messageContent: string): Promise<boolean> => {
    console.log('[useChat.sendMessage] Called with:', { messageContent: messageContent.substring(0, 50), isLoading });
    if (!messageContent.trim() || isLoading) {
      console.log('[useChat.sendMessage] Early return - empty message or isLoading:', { isEmpty: !messageContent.trim(), isLoading });
      return false;
    }

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageContent,
    };

    // Optimistically update the UI with the user's message
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      let responseData;

      // Support both new API (apiEndpoint) and legacy API (handler)
      if (handler) {
        // Legacy mode: handler expects full messages array including the new user message
        // For history fidelity, use rawContent for assistant messages if available
        const messagesForHandler = [...messages, userMessage].map(msg => ({
          role: msg.role,
          content:
            msg.role === 'assistant' && msg.rawContent
              ? msg.rawContent
              : msg.content,
        }));
        responseData = await handler(messagesForHandler as ChatMessage[], abortController.signal);
      } else if (apiEndpoint) {
        // New mode: use apiEndpoint
        // Generate and track message ID for cancellation
        const messageId = userMessage.id;
        activeMessageIdRef.current = messageId;

        const response = await apiClient.post(apiEndpoint, {
          message: messageContent,
          id: messageId // Pass ID to backend for tracking
        }, { signal: abortController.signal });
        responseData = response.data;
      } else {
        throw new Error('Either apiEndpoint or handler must be provided');
      }

      const responseContent = onResponse(responseData);

      if (responseContent) {
        const baseMessage: ChatMessage = {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: '',
        };

        const assistantMessage = typeof responseContent === 'string'
          ? { ...baseMessage, content: responseContent }
          : { ...baseMessage, ...responseContent };

        // Update the UI with the assistant's response
        setMessages((prevMessages) => [...prevMessages, assistantMessage]);
      }
      return true; // Success
    } catch (err: any) {
      // Check for abort error
      // Check for abort error - Robust check using signal or error type
      if (
        abortController.signal.aborted ||
        err.name === 'AbortError' ||
        err.code === 'ECONNABORTED' ||
        (err.originalError && err.originalError.name === 'AbortError')
      ) {
        console.log('Request aborted (Caught in sendMessage)');
        // Restore state "atomically"
        setMessages((prev) => prev.slice(0, -1)); // Pop optimistic message
        setInput(messageContent); // Restore input for editing
        return false;
      }

      const errorMessage =
        err.response?.status === 500
          ? 'Error processing your request, please try again.'
          : err.response?.data?.message || err.message || 'An unexpected error occurred.';
      setError({ message: errorMessage, isInitError: false });
      // Revert the optimistic update on failure
      setMessages((prev) => prev.slice(0, -1));
      setInput(messageContent);
      return false; // Failure
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
      activeMessageIdRef.current = null; // Clear active ID on completion
    }
  }, [apiEndpoint, handler, isLoading, onResponse, messages]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await sendMessage(input);
    if (success) {
      setInput('');
      // Refocus the input after successful submission
      setTimeout(() => {
        inputRef?.current?.focus();
      }, 0);
    }
  }, [sendMessage, input, inputRef]);

  return {
    messages,
    setMessages,
    input,
    handleInputChange,
    handleSubmit,
    sendMessage,
    isLoading,
    error,
    retryInit,
    stop,
  };
};
